import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { Button } from "@redotech/redo-web/components/ui/button";
import { Separator } from "@redotech/redo-web/components/ui/separator";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context";

export interface CheckoutSuccessProps {
  onContinueShopping?: () => void;
}

export const CheckoutSuccess = memo(function CheckoutSuccess({
  onContinueShopping,
}: CheckoutSuccessProps) {
  const navigate = useNavigate();
  const { clearCart } = useCart();

  // Clear the cart when the success page loads
  useLoad(async () => {
    await clearCart();
  }, [clearCart]);

  const handleContinueShopping = useHandler(() => {
    if (onContinueShopping) {
      onContinueShopping();
    } else {
      navigate("/");
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-12 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
          <div className="text-center space-y-6">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12L11 14L15 10"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </div>

            {/* Main Content */}
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Payment Successful!
              </h1>

              <p className="text-base text-gray-600">
                Thank you for your purchase. Your order has been confirmed.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-md p-3 mt-4">
                <p className="text-sm text-green-800">
                  You'll receive an email confirmation shortly with your order
                  details.
                </p>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Action Button */}
            <div>
              <Button
                className="w-full"
                onClick={handleContinueShopping}
                size="lg"
              >
                Continue Shopping
              </Button>
            </div>

            <Separator className="my-6" />

            {/* Support Info */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Need help?{" "}
                <a
                  className="text-blue-600 hover:text-blue-500 underline"
                  href="mailto:support@redo.com"
                >
                  Contact support@redo.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
